/**
 * hk_os · <TaskProgressCard>
 * ────────────────────────────────────────────────────────────────────
 * Hero operational card for the CURRENT task. Room number is the largest
 * element; the card holds a single primary CTA. All secondary info sits
 * as glanceable meta.
 *
 * Props:
 *   roomNumber   — required (string / number)
 *   floorLabel   — optional short floor / wing string
 *   taskTypeKey  — voice snippet key for the task type (e.g. VOICE_ROOM_DIRTY)
 *   badges       — [{label, tone}]  tone: "vip" | "priority" | "info" | "warning"
 *   etaMinutes   — optional integer
 *   progress     — 0..1
 *   primaryCta   — {label, icon, onClick, tone}  (uses hk_os TaskCompletionButton)
 *   testid
 *
 * NO business logic. Purely presentational.
 */
import React from "react";
import { Crown, AlertTriangle, Info, ShieldAlert, Timer } from "lucide-react";
import { VoicePlayer } from "./VoicePlayer";
import { TaskCompletionButton } from "./TaskCompletionButton";

const BADGE_TONE = {
  vip:      { cls: "bg-amber-100 text-amber-900 border-amber-300",  icon: Crown },
  priority: { cls: "bg-red-100 text-red-900 border-red-300",        icon: AlertTriangle },
  info:     { cls: "bg-sky-100 text-sky-900 border-sky-300",        icon: Info },
  warning:  { cls: "bg-orange-100 text-orange-900 border-orange-300", icon: ShieldAlert },
};

export function TaskProgressCard({
  roomNumber,
  floorLabel,
  taskTypeKey,
  badges = [],
  etaMinutes,
  progress = 0,
  primaryCta,
  testid,
}) {
  const pct = Math.max(0, Math.min(1, progress));
  return (
    <section
      data-testid={testid || "task-progress-card"}
      className={[
        "relative overflow-hidden rounded-2xl border-2 border-slate-200 bg-white",
        "shadow-sm p-5 sm:p-6 flex flex-col gap-4",
      ].join(" ")}
      aria-labelledby={testid ? `${testid}-room` : "task-progress-room"}
    >
      {/* Top row — badges + floor */}
      <div className="flex flex-wrap items-center gap-2">
        {badges.map((b, i) => {
          const t = BADGE_TONE[b.tone] || BADGE_TONE.info;
          const Icon = t.icon;
          return (
            <span
              key={`${b.label}-${i}`}
              data-testid={testid ? `${testid}-badge-${b.tone}` : `task-badge-${b.tone}`}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[12px] font-semibold ${t.cls}`}
            >
              <Icon className="w-3 h-3" strokeWidth={2.6} />
              {b.label}
            </span>
          );
        })}
        {floorLabel && (
          <span className="ml-auto text-[13px] font-medium text-slate-500">
            {floorLabel}
          </span>
        )}
      </div>

      {/* Hero — room number */}
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <div className="text-[13px] font-semibold uppercase tracking-widest text-slate-500">
            Room
          </div>
          <div
            id={testid ? `${testid}-room` : "task-progress-room"}
            data-testid={testid ? `${testid}-number` : "task-progress-number"}
            className="text-[72px] sm:text-[96px] font-black leading-[0.95] text-slate-900"
          >
            {roomNumber ?? "—"}
          </div>
          {taskTypeKey && (
            <div className="mt-3">
              <VoicePlayer
                voiceKey={taskTypeKey}
                size="md"
                showText
                testid={testid ? `${testid}-task-voice` : "task-progress-task-voice"}
              />
            </div>
          )}
        </div>
        {typeof etaMinutes === "number" && (
          <div
            data-testid={testid ? `${testid}-eta` : "task-progress-eta"}
            className="text-right"
          >
            <div className="inline-flex items-center gap-1 text-[13px] font-semibold uppercase tracking-widest text-slate-500">
              <Timer className="w-3.5 h-3.5" strokeWidth={2.4} /> ETA
            </div>
            <div className="text-[28px] font-bold text-slate-800 leading-tight">
              {etaMinutes}
              <span className="text-[14px] font-semibold text-slate-500 ml-1">min</span>
            </div>
          </div>
        )}
      </div>

      {/* Progress rail */}
      <div className="flex items-center gap-3">
        <div className="text-[12px] font-semibold uppercase tracking-widest text-slate-500 w-20">
          Progress
        </div>
        <div className="h-2.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
          <div
            data-testid={testid ? `${testid}-progress` : "task-progress-progress"}
            style={{ width: `${pct * 100}%` }}
            className="h-full bg-sky-500 transition-[width] duration-300"
          />
        </div>
        <div className="text-[14px] font-semibold text-slate-700 tabular-nums w-12 text-right">
          {Math.round(pct * 100)}%
        </div>
      </div>

      {/* Primary CTA */}
      {primaryCta && (
        <TaskCompletionButton
          {...primaryCta}
          testid={testid ? `${testid}-cta` : "task-progress-cta"}
        />
      )}
    </section>
  );
}

export default TaskProgressCard;
